import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Download, Store } from "lucide-react";
import api from "../lib/api";

const n = (v) => Number(v || 0).toLocaleString("es-DO");

function DealerLeads({ dealer, params }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/leads-digital/dealer-leads", { params: { ...params, dealer } })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], total: 0 }));
  }, [dealer, params]);
  if (!data) return <div className="h-16 m-3 rounded-lg bg-gray-50 animate-pulse"/>;
  return (
    <div className="p-3 bg-gray-50" data-testid={`dealer-leads-${dealer}`}>
      <div className="max-h-80 overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs whitespace-nowrap">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b">
              {["Nombre", "Fecha", "Marca", "Modelo", "Estado", "Campaña"].map((h) => (
                <th key={h} className="text-left py-1.5 px-2 font-extrabold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1.5 px-2 font-semibold text-gray-800">{r.name}</td>
                <td className="py-1.5 px-2 tabular-nums text-gray-600 whitespace-nowrap">{r.date}</td>
                <td className="py-1.5 px-2">{r.brand || "—"}</td>
                <td className="py-1.5 px-2">{r.model || "—"}</td>
                <td className="py-1.5 px-2">{r.status || "—"}</td>
                <td className="py-1.5 px-2 text-gray-600 max-w-[220px] truncate" title={r.campaign}>{r.campaign || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.total > data.rows.length && (
        <div className="text-[11px] text-gray-500 font-semibold mt-1.5">Mostrando los {n(data.rows.length)} más recientes de {n(data.total)}.</div>
      )}
    </div>
  );
}

export default function DealersExternalTable({ data, params }) {
  const [open, setOpen] = useState(null);
  const dealers = data?.dealers || [];
  const total = data?.total || 0;
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/leads-digital/dealer-leads/export-csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `dealers_externos_leads_${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <section className="surface p-4 md:p-6 mb-5" data-testid="lp2-dealers-external">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Store size={18} style={{ color: "#3b82f6" }}/>
            <h2 className="text-lg font-extrabold font-display">Detalle de Dealers Externos</h2>
          </div>
          <p className="text-xs text-gray-500 font-semibold mt-0.5 mb-3">
            {n(total)} leads de la Red de Dealers Externos · haz clic en un dealer para ver sus leads
          </p>
        </div>
        <button onClick={download} disabled={!dealers.length || busy} data-testid="lp2-dealers-csv"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0"
                style={{ transition: "background-color 150ms ease" }}>
          <Download size={13}/> {busy ? "Descargando..." : "CSV"}
        </button>
      </div>
      {!dealers.length ? (
        <div className="text-sm text-gray-400 font-semibold py-4" data-testid="lp2-dealers-empty">Sin leads de dealers externos para estos filtros.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-400 border-b">
                <th className="text-left py-2 px-2 font-extrabold">Dealer</th>
                <th className="text-right py-2 px-2 font-extrabold w-20 sm:w-28">Leads</th>
                <th className="text-right py-2 px-2 font-extrabold w-24 sm:w-32">% del total</th>
              </tr>
            </thead>
            <tbody>
              {dealers.map((d, i) => {
                const pct = total ? Math.round((d.count / total) * 1000) / 10 : 0;
                const isOpen = open === d.dealer_name;
                return (
                  <Fragment key={d.dealer_name}>
                    <tr className="border-b cursor-pointer hover:bg-gray-50" style={{ transition: "background-color 120ms ease" }}
                        onClick={() => setOpen(isOpen ? null : d.dealer_name)} data-testid={`lp2-dealer-row-${i}`}>
                      <td className="py-2 px-2 font-semibold text-gray-800">
                        <span className="inline-flex items-center gap-1.5">
                          {isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}{d.dealer_name}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums font-bold">{n(d.count)}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-gray-600">{pct}%</td>
                    </tr>
                    {isOpen && (
                      <tr><td colSpan={3} className="p-0"><DealerLeads dealer={d.dealer_name} params={params}/></td></tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
